/**
 * /notifications — Actionable inbox (email-style). Surface Layer (/01)
 *
 * Not just a list: the project's command surface. A notification can NOTIFY,
 * carry an APPROVAL/DECISION (individual, tenant-wide GLOBAL, or GENERAL
 * broadcast), DOCUMENT an artifact, or EXECUTE an agentic action. Visibility +
 * actions are gated by access level server-side (RLS + notification_act role_rank
 * × decision_scope). When the user lacks authority, the affordance becomes
 * "Delegate up" (escalate) instead of acting. Email affordances: Inbox / Unread /
 * Archived views, archive/unarchive, mark-read. Authority shown here is
 * presentation-only — notification_act always re-enforces it.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Column,
  ContentSwitcher,
  Grid,
  InlineNotification,
  Modal,
  OverflowMenu,
  OverflowMenuItem,
  SkeletonText,
  Switch,
  Tag,
  TextArea,
  TextInput,
  Tile,
} from '@carbon/react';
import { CheckmarkOutline, Close, Play, Document as DocumentIcon, Events } from '@carbon/icons-react';
import type { NotificationRow } from '@contracts/runtime-endpoints.contract';
import { useNotifications, useMe, type NotificationsView } from '@lib/persistence/useRuntimeData';
import { SecretRequestInboxPanel } from '../features/secrets/SecretRequestInboxPanel';
import '../styles/views.scss';

export const Route = createFileRoute('/notifications')({
  component: NotificationsInboxView,
});

type Category = NonNullable<NotificationRow['category']>;

const CATEGORY_TAG: Partial<Record<Category, 'red' | 'magenta' | 'purple' | 'blue' | 'cyan' | 'teal' | 'green' | 'gray' | 'cool-gray' | 'warm-gray'>> = {
  approval: 'magenta',
  decision: 'purple',
  dev_action: 'purple',
  release: 'teal',
  report: 'cyan',
  document: 'blue',
  status: 'cool-gray',
  task: 'blue',
  ai: 'teal',
  plugin: 'warm-gray',
  ops: 'green',
  system: 'gray',
};

const PRIORITY_TAG: Record<string, 'red' | 'magenta' | 'gray' | 'cool-gray'> = {
  critical: 'red',
  high: 'magenta',
  normal: 'cool-gray',
  low: 'gray',
};

const GROUPS: Array<{ key: string; label: string; cats: Category[] }> = [
  { key: 'approvals', label: 'Approvals & decisions', cats: ['approval', 'decision'] },
  { key: 'dev', label: 'Developer actions', cats: ['dev_action'] },
  { key: 'docs', label: 'Reports & documents', cats: ['release', 'report', 'document'] },
  { key: 'updates', label: 'Updates', cats: ['status', 'task', 'ai', 'plugin', 'ops', 'system'] },
];

const ROLE_RANK: Record<string, number> = { owner: 5, admin: 4, developer: 3, user: 2, viewer: 1 };
function roleRank(role: string | null | undefined): number {
  return ROLE_RANK[role ?? 'user'] ?? 2;
}
/** Mirror idl_private.role_rank × decision_scope (global ⇒ ≥ admin). Presentation-only. */
function requiredRankFor(n: NotificationRow): number {
  return Math.max(roleRank(n.required_role ?? 'user'), n.decision_scope === 'global' ? (ROLE_RANK.admin ?? 4) : 0);
}

const VIEWS: NotificationsView[] = ['inbox', 'unread', 'archived'];

/** Content limits (REQ-001.65) — mirror idl_private.word_count + notifications_*_chk. */
const TITLE_MAX = 120;
const PREVIEW_MAX_WORDS = 20;
const BODY_MAX_WORDS = 300;
function wordCount(text: string): number {
  const s = text.trim();
  return s === '' ? 0 : s.split(/\s+/).length;
}

function NotificationsInboxView() {
  const [view, setView] = useState<NotificationsView>('inbox');
  const { notifications, unreadCount, openActionable, delegatedCount, isLoading, tenantContextMissing, ack, act, delegate, archive, createClientGlobalNotification } =
    useNotifications(undefined, 100, view);
  const { me } = useMe();
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'info' | 'error'; text: string } | null>(null);

  // Composer for client_global notices (v2). Persistence is gated server-side
  // (migration_apply_pending) — the UI surfaces that honestly, never as persisted.
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState({ title: '', previewText: '', body: '' });
  const titleInvalid = draft.title.length > TITLE_MAX;
  const previewInvalid = wordCount(draft.previewText) > PREVIEW_MAX_WORDS;
  const bodyInvalid = wordCount(draft.body) > BODY_MAX_WORDS;
  const composerInvalid = draft.title.trim().length < 1 || titleInvalid || previewInvalid || bodyInvalid;

  async function onCreateClientGlobal() {
    const res = await createClientGlobalNotification({
      title: draft.title.trim(),
      preview_text: draft.previewText.trim() || undefined,
      body: draft.body.trim() || undefined,
      decision_scope: 'general',
    });
    setComposerOpen(false);
    setDraft({ title: '', previewText: '', body: '' });
    if (res.ok) {
      setFeedback({ kind: 'success', text: 'Client-global notice published.' });
    } else if (res.status === 'migration_apply_pending') {
      setFeedback({ kind: 'info', text: 'Validated, not yet persisted — backend migration pending (migration_apply_pending).' });
    } else {
      setFeedback({ kind: 'error', text: `Could not publish (${res.status}).` });
    }
  }

  const role = me?.role ?? 'user';
  const callerRank = roleRank(role);
  const isElevated = role === 'owner' || role === 'admin' || role === 'developer';

  async function onAck(n: NotificationRow) {
    setBusy(n.id);
    const ok = await ack(n.id);
    setBusy(null);
    if (!ok) setFeedback({ kind: 'error', text: 'Could not mark as read.' });
  }

  async function onArchive(n: NotificationRow, archived: boolean) {
    setBusy(n.id);
    const ok = await archive(n.id, archived);
    setBusy(null);
    if (!ok) setFeedback({ kind: 'error', text: `Could not ${archived ? 'archive' : 'unarchive'}.` });
  }

  async function onAct(n: NotificationRow, decision: string) {
    setBusy(n.id);
    const r = decision === 'delegate' ? await delegate(n.id) : await act(n.id, decision);
    setBusy(null);
    if (!r.ok) {
      setFeedback({ kind: 'error', text: 'Action not permitted or failed.' });
      return;
    }
    if (r.status === 'delegated') {
      setFeedback({ kind: 'info', text: `Escalated to ${r.delegatedToRole ?? 'a higher role'} — someone with that authority will decide.` });
      return;
    }
    const globalNote = r.global ? ' Tenant-wide decision — applies to all related items.' : '';
    setFeedback({
      kind: 'success',
      text: (r.dispatchedIntent ? `Dispatched ${r.dispatchedIntent} — the worker will process it.` : `Recorded: ${decision}.`) + globalNote,
    });
  }

  function scopeBadges(n: NotificationRow) {
    return (
      <>
        {n.client_visibility === 'client_global' ? <Tag type="green">client-global</Tag> : null}
        {n.decision_scope === 'global' ? <Tag type="red">tenant-wide</Tag> : null}
        {n.decision_scope === 'general' ? <Tag type="teal">broadcast</Tag> : null}
        {n.audience === 'subtree' ? <Tag type="cyan">to subtree</Tag> : null}
        {n.delegated_to_role ? <Tag type="magenta">escalated → {n.delegated_to_role}</Tag> : null}
      </>
    );
  }

  function actionsFor(n: NotificationRow) {
    const overflow = (
      <OverflowMenu size="sm" aria-label="More" flipped>
        {view === 'archived' ? (
          <OverflowMenuItem itemText="Unarchive" disabled={busy === n.id} onClick={() => void onArchive(n, false)} />
        ) : (
          <OverflowMenuItem itemText="Archive" disabled={busy === n.id} onClick={() => void onArchive(n, true)} />
        )}
        {!n.read_at ? <OverflowMenuItem itemText="Mark read" disabled={busy === n.id} onClick={() => void onAck(n)} /> : null}
      </OverflowMenu>
    );

    if (n.status && n.status !== 'open') {
      return (
        <div className="notification-row__meta">
          <Tag type="cool-gray">{n.acted_result ?? n.status}</Tag>
          {overflow}
        </div>
      );
    }

    const kind = n.action_kind ?? 'notify';
    const needsDelegate = callerRank < requiredRankFor(n) || (!isElevated && (n.decision_scope === 'global' || n.actor_scope === 'hubdev'));

    if (kind === 'approve' || kind === 'decision' || kind === 'execute') {
      return (
        <div className="view-actions">
          {needsDelegate ? (
            <Button size="sm" renderIcon={Events} disabled={busy === n.id} onClick={() => void onAct(n, 'delegate')}>
              Delegate up
            </Button>
          ) : kind === 'execute' ? (
            <Button size="sm" renderIcon={Play} disabled={busy === n.id} onClick={() => void onAct(n, 'execute')}>
              Run
            </Button>
          ) : (
            <>
              <Button size="sm" renderIcon={CheckmarkOutline} disabled={busy === n.id} onClick={() => void onAct(n, 'approve')}>
                Approve
              </Button>
              <Button size="sm" kind="danger--tertiary" renderIcon={Close} disabled={busy === n.id} onClick={() => void onAct(n, 'reject')}>
                Reject
              </Button>
            </>
          )}
          {overflow}
        </div>
      );
    }

    if (kind === 'document' && n.related_artifact) {
      return (
        <div className="notification-row__meta">
          <DocumentIcon size={16} aria-hidden="true" />
          <Tag type="blue">{n.related_artifact}</Tag>
          {overflow}
        </div>
      );
    }
    return <div className="notification-row__meta">{overflow}</div>;
  }

  function renderTile(n: NotificationRow) {
    return (
      <Tile key={n.id} className={n.read_at ? 'view-tile' : 'view-tile view-tile--unread'}>
        <div className="view-tile__row">
          <strong>{n.title}</strong>
          <span>
            {n.category ? <Tag type={CATEGORY_TAG[n.category] ?? 'gray'}>{n.category}</Tag> : null}
            {n.priority && n.priority !== 'normal' ? <Tag type={PRIORITY_TAG[n.priority] ?? 'gray'}>{n.priority}</Tag> : null}
            {n.actor_scope === 'hubdev' ? <Tag type="purple">operator</Tag> : null}
            {scopeBadges(n)}
          </span>
        </div>
        {n.preview_text ? <p className="view-tile__label"><em>{n.preview_text}</em></p> : null}
        {n.body ? <p className="view-tile__label">{n.body}</p> : null}
        {actionsFor(n)}
      </Tile>
    );
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Inbox</h1>
        <p className="view__subtitle">
          {unreadCount} unread · {openActionable} awaiting action · {delegatedCount} escalated · role: {role}
          {isElevated ? ' · operator actions enabled' : ''}
        </p>
      </header>

      <ContentSwitcher
        selectedIndex={VIEWS.indexOf(view)}
        onChange={({ index }: { index?: number }) => setView(VIEWS[index ?? 0] ?? 'inbox')}
      >
        <Switch name="inbox" text="Inbox" />
        <Switch name="unread" text="Unread" />
        <Switch name="archived" text="Archived" />
      </ContentSwitcher>

      {isElevated ? (
        <div className="view-actions">
          <Button size="sm" kind="tertiary" onClick={() => setComposerOpen(true)}>
            New client-global notice
          </Button>
        </div>
      ) : null}

      {isElevated ? <SecretRequestInboxPanel /> : null}

      {feedback ? (
        <InlineNotification
          kind={feedback.kind}
          lowContrast
          title={feedback.kind === 'success' ? 'Done' : feedback.kind === 'error' ? 'Blocked' : 'Info'}
          subtitle={feedback.text}
          onClose={() => setFeedback(null)}
        />
      ) : null}

      {tenantContextMissing ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Workspace being provisioned"
          subtitle="Your inbox appears once your workspace context is ready."
        />
      ) : isLoading && notifications.length === 0 ? (
        <SkeletonText paragraph lineCount={5} />
      ) : notifications.length === 0 ? (
        <Tile className="view-empty">
          <h2>{view === 'archived' ? 'No archived items' : view === 'unread' ? 'Nothing unread' : 'Inbox empty'}</h2>
          <p className="view-tile__label">Approvals, reports and agentic actions will appear here.</p>
        </Tile>
      ) : (
        GROUPS.map((g) => {
          const items = notifications.filter((n) => g.cats.includes((n.category ?? 'system') as Category));
          if (items.length === 0) return null;
          return (
            <section key={g.key} className="view__section">
              <h2 className="view-tile__value">
                {g.label} <Tag type="cool-gray">{items.length}</Tag>
              </h2>
              <div className="surface-grid">
                <Grid condensed>
                  {items.map((n) => (
                    <Column key={n.id} sm={4} md={8} lg={16}>
                      {renderTile(n)}
                    </Column>
                  ))}
                </Grid>
              </div>
            </section>
          );
        })
      )}

      <Modal
        open={composerOpen}
        modalHeading="New client-global notice"
        primaryButtonText="Publish"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={composerInvalid}
        onRequestClose={() => setComposerOpen(false)}
        onRequestSubmit={() => void onCreateClientGlobal()}
      >
        <p className="view-tile__label">
          Visible to Client Workroom users (client_global). Limits: title ≤ {TITLE_MAX} chars · preview ≤ {PREVIEW_MAX_WORDS} words · body ≤ {BODY_MAX_WORDS} words.
        </p>
        <TextInput
          id="cg-title"
          labelText="Title"
          maxLength={TITLE_MAX}
          invalid={titleInvalid}
          invalidText="Title is too long."
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <TextArea
          id="cg-preview"
          labelText={`Preview (${wordCount(draft.previewText)}/${PREVIEW_MAX_WORDS} words)`}
          rows={2}
          invalid={previewInvalid}
          invalidText="Preview is over 20 words."
          value={draft.previewText}
          onChange={(e) => setDraft({ ...draft, previewText: e.target.value })}
        />
        <TextArea
          id="cg-body"
          labelText={`Body (${wordCount(draft.body)}/${BODY_MAX_WORDS} words)`}
          rows={4}
          invalid={bodyInvalid}
          invalidText="Body is over 300 words."
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
        />
      </Modal>
    </div>
  );
}
